import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { SettingsSection } from '../../components/SectionNav';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import {
  useBrandRulesHistoryQuery,
  useBrandRulesQuery,
  useUpdateBrandRulesMutation,
} from '../../services/brandRulesService/__generated__/queries.generated';
import { Button } from '../../components/button';
import SwitchComponent from '../../components/switch';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  EmptyState,
  LoadingBlock,
} from '../../components/ui';
import { toastActions } from '../../../store/toastStore';
import {
  ALL_CAPABILITIES_ON,
  HostCapabilities,
  normalizeCapabilities,
} from '../../utils/hostCapabilities';
import {
  CAPABILITY_GROUPS,
  CAPABILITY_INFO,
  changedCapabilities,
  describeRulesChange,
} from './hostCapabilitiesAdmin';

const Row = styled.label`
  ${tw`flex items-start justify-between gap-4 py-3 cursor-pointer`}
  & + & {
    border-top: 1px solid var(--color-border);
  }
`;
const Text = styled.div`
  ${tw`min-w-0`}
`;
const Title = styled.div`
  ${tw`text-sm font-medium`}
  color: var(--color-text-primary);
`;
const Hint = styled.div`
  ${tw`text-xs mt-0.5`}
  color: var(--color-text-secondary);
`;
const Warn = styled.div`
  ${tw`text-xs mt-1`}
  color: #b45309;
`;
const GroupTitle = styled.div`
  ${tw`text-xs font-semibold uppercase tracking-wide pt-4 pb-1`}
  /* Secondary, not tertiary: tertiary gray on white is 2.5:1 (axe, spec 030 T013). */
  color: var(--color-text-secondary);
  &:first-child {
    padding-top: 0;
  }
`;
const HistoryRow = styled.li`
  ${tw`py-3 text-sm`}
  & + & {
    border-top: 1px solid var(--color-border);
  }
`;
const HistoryMeta = styled.div`
  ${tw`text-xs mb-1`}
  color: var(--color-text-secondary);
`;

function isStale(error: unknown): boolean {
  const graphQLErrors = (error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> })
    ?.graphQLErrors;
  return Boolean(graphQLErrors?.some(e => e.extensions?.code === 'BRAND_RULES_STALE'));
}

/**
 * Spec 014a Phase D (T034, T035): this brand's host switches and their change
 * log, on `/admin/brand-settings`. The switches apply to every host on this
 * site; another brand's switches are not shown or changed here.
 */
export function HostCapabilitiesSection({
  brandName,
  permissionsId,
  historyId,
}: {
  brandName: string;
  /** Spec 030 anchors, so the page nav can jump to each card. */
  permissionsId?: string;
  historyId?: string;
}) {
  const { data, loading, error, refetch } = useBrandRulesQuery({ fetchPolicy: 'network-only' });
  const history = useBrandRulesHistoryQuery({
    variables: { limit: 50 },
    fetchPolicy: 'network-only',
  });
  const [updateBrandRules, { loading: saving }] = useUpdateBrandRulesMutation();

  const loaded = useMemo<HostCapabilities>(
    () =>
      data?.brandRules
        ? normalizeCapabilities(data.brandRules.hostCapabilities)
        : ALL_CAPABILITIES_ON,
    [data],
  );
  const [edited, setEdited] = useState<HostCapabilities>(loaded);
  useEffect(() => setEdited(loaded), [loaded]);
  const changes = changedCapabilities(loaded, edited);
  const dirty = Object.keys(changes).length > 0;

  const toggle = (key: keyof HostCapabilities, on: boolean) => {
    const info = CAPABILITY_INFO.find(item => item.key === key);
    if (!on && info?.notReady) {
      toastActions.showToastAlert(
        `Turn off ${info.title.toLowerCase()}?`,
        info.notReady,
        'You can turn it back on at any time.',
        () => setEdited(current => ({ ...current, [key]: false })),
        () => undefined,
        'Turn off',
        'Keep on',
      );
      return;
    }
    setEdited(current => ({ ...current, [key]: on }));
  };

  const save = async () => {
    if (!data?.brandRules || !dirty) return;
    try {
      await updateBrandRules({
        variables: { input: { version: data.brandRules.version, hostCapabilities: changes } },
      });
      toast.success('Saved. Hosts see the change within about a minute.');
      await Promise.all([refetch(), history.refetch()]);
    } catch (e) {
      if (isStale(e)) {
        toast.error(
          'Someone else changed these switches. The latest values are loaded; check them and save again.',
        );
        await refetch();
        return;
      }
      toast.error((e as Error).message || 'Could not save host permissions');
    }
  };

  return (
    <>
      <SettingsSection id={permissionsId} label="Host permissions">
        <Card>
          <CardHeader
            title="Host permissions"
            description={`What hosts may manage on ${brandName}. A switch that is off hides those pages and buttons from hosts here, and the server refuses them; you and other ${brandName} admins still see them, marked "Admin only". It does not change other brands, even for a host who is approved on both. Car page settings are under Car settings.`}
          />
          {loading && <LoadingBlock label="Loading host permissions" />}
          {error && (
            <CardBody>
              <Alert tone="error" title="Could not load host permissions">
                {error.message}
              </Alert>
            </CardBody>
          )}
          {!loading && !error && (
            <CardBody>
              {CAPABILITY_GROUPS.map(({ group, title }) => (
                <div key={group}>
                  <GroupTitle>{title}</GroupTitle>
                  {CAPABILITY_INFO.filter(info => info.group === group).map(info => (
                    <Row key={info.key}>
                      <Text>
                        <Title>{info.title}</Title>
                        <Hint>Off hides: {info.hides}</Hint>
                        {info.notReady && <Warn>Not ready to turn off: {info.notReady}</Warn>}
                      </Text>
                      <SwitchComponent
                        isActive={edited[info.key]}
                        onChange={e => toggle(info.key, e.target.checked)}
                        inputTestId={`host-capability-${info.key}`}
                        ariaLabel={info.title}
                      />
                    </Row>
                  ))}
                </div>
              ))}
            </CardBody>
          )}
          <CardFooter>
            <Button
              theme="filled"
              text="Save host permissions"
              onClick={save}
              isDisabled={!dirty || saving}
              isLoading={saving}
            />
          </CardFooter>
        </Card>
      </SettingsSection>

      <SettingsSection id={historyId} label="Change history">
        <Card>
          <CardHeader
            title="Change history"
            description="Every change to this brand's rules and host permissions."
          />
          <CardBody>
            {history.loading && <LoadingBlock label="Loading history" />}
            {!history.loading && (history.data?.brandRulesHistory ?? []).length === 0 && (
              <EmptyState title="No changes yet" />
            )}
            <ul data-testid="brand-rules-history">
              {(history.data?.brandRulesHistory ?? []).map(row => (
                <HistoryRow key={row.id}>
                  <HistoryMeta>
                    {DateTime.fromMillis(row.changedAt).toLocaleString(DateTime.DATETIME_MED)} ·{' '}
                    {row.changedBy?.name || row.changedBy?.email || 'Setup'} ·{' '}
                    {row.field === 'HOST_CAPABILITIES' ? 'Host permissions' : 'Booking rules'}
                  </HistoryMeta>
                  {describeRulesChange(row.before, row.after).map(line => (
                    <div key={line}>{line}</div>
                  ))}
                </HistoryRow>
              ))}
            </ul>
          </CardBody>
        </Card>
      </SettingsSection>
    </>
  );
}

export default HostCapabilitiesSection;
